import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

const useCurricularCalendars = () => {
  const navigate = useNavigate();
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCalendars = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const queryObj = {};
        if (params.termID) queryObj.termID = params.termID;
        if (params.acadyrID) queryObj.acadyrID = params.acadyrID;
        if (params.status) queryObj.status = params.status;

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.academics.curricularCalendars}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setCalendars(
            Array.isArray(response.data)
              ? response.data
              : response.data?.results || [],
          );
        } else {
          Utilities.showError(response, "Failed to fetch calendars");
        }
      } catch (error) {
        message.error("Error fetching calendars");
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const createCalendar = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.academics.curricularCalendars,
          data,
          navigate,
        );
        if (response.ok) {
          Utilities.showNotification(
            undefined,
            "Calendar created",
            "text-success",
          );
          return response.data;
        }
        Utilities.showError(response, "Failed to create calendar");
        return null;
      } catch (error) {
        message.error("Error creating calendar");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const updateCalendar = useCallback(
    async (id, data) => {
      setLoading(true);
      try {
        const url =
          domainSettings.urls.academics.curricularCalendarDetail.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "PATCH",
          url,
          data,
          navigate,
        );
        if (response.ok) {
          Utilities.showNotification(
            undefined,
            "Calendar updated",
            "text-success",
          );
          return response.data;
        }
        Utilities.showError(response, "Failed to update calendar");
        return null;
      } catch (error) {
        message.error("Error updating calendar");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const deleteCalendar = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const url =
          domainSettings.urls.academics.curricularCalendarDetail.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          Utilities.showNotification(
            undefined,
            "Calendar deleted",
            "text-success",
          );
          return true;
        }
        Utilities.showError(response, "Failed to delete calendar");
        return false;
      } catch (error) {
        message.error("Error deleting calendar");
        console.error(error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const submitCalendar = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const url =
          domainSettings.urls.academics.curricularCalendarSubmit.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          {},
          navigate,
        );
        if (response.ok) {
          Utilities.showNotification(
            undefined,
            "Calendar submitted for approval",
            "text-success",
          );
          return response.data;
        }
        Utilities.showError(
          response,
          response.data?.detail || "Failed to submit calendar",
        );
        return null;
      } catch (error) {
        message.error("Error submitting calendar");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const approveCalendar = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const url =
          domainSettings.urls.academics.curricularCalendarApprove.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          {},
          navigate,
        );
        if (response.ok) {
          Utilities.showNotification(
            undefined,
            "Calendar approved",
            "text-success",
          );
          return response.data;
        }
        Utilities.showError(
          response,
          response.data?.detail || "Failed to approve calendar",
        );
        return null;
      } catch (error) {
        message.error("Error approving calendar");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const rejectCalendar = useCallback(
    async (id, reason) => {
      setLoading(true);
      try {
        const url =
          domainSettings.urls.academics.curricularCalendarReject.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { reason },
          navigate,
        );
        if (response.ok) {
          Utilities.showNotification(
            undefined,
            "Calendar rejected",
            "text-success",
          );
          return response.data;
        }
        Utilities.showError(
          response,
          response.data?.detail || "Failed to reject calendar",
        );
        return null;
      } catch (error) {
        message.error("Error rejecting calendar");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  return {
    calendars,
    loading,
    fetchCalendars,
    createCalendar,
    updateCalendar,
    deleteCalendar,
    submitCalendar,
    approveCalendar,
    rejectCalendar,
  };
};

export default useCurricularCalendars;
